import React from 'react';
import PropTypes from 'prop-types';
import {QuestionFooter} from '../SharedComponents/QuestionFooter';
import {QUESTION_CONTENTS} from '@learningblocks/exp-constants/lib/Utilities';
import {Question} from '../SharedComponents/Question';
import {SliderGroup} from '../SharedComponents/SliderGroup';
import {FooterFeedback} from '../SharedComponents/FooterFeedback';
import '../SharedComponents/CommonStyles';

/**
 * Experimental-Design Question component [Slider-Select-Group]:
 *  - You have multiple groups with different number of character sequences
 *  - You will have to select the correct highlight in each character-sequence to answer the question.
 */
export function SliderSelectGroup(props) {
  const isFeedbackCorrect = () => {
    const isCorrect = props.sliderGroups.every((sliderGroup) => {
      return sliderGroup.sliders.every(
        (slider) =>
          slider.current.start === slider.answer.start &&
          slider.current.end === slider.answer.end
      );
    });
    return isCorrect ? true : false;
  };

  const setSubmitDisabled = () => {
    if (props.complete) return true;
    else {
      let updated = props.sliderGroups.some((slider) => slider.updated);
      if (updated) return false;
      else return true;
    }
  };

  const renderSliderGroups = (key) => {
    return props.sliderGroups.map((slider, id) => (
      <SliderGroup
        {...slider}
        label={id + 1}
        key={`${key}_${id}`}
        componentID={props.id}
        changeHighlight={props.updateSelection}
      />
    ));
  };

  const renderQuestion = (key) => (
    <Question
      key={key}
      className={`${props.className}__text`}
      question={props.question}
    />
  );

  const renderByType = (type, key) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key);
      case QUESTION_CONTENTS.SLIDER_GROUPS:
        return renderSliderGroups(key);
      default:
        return null;
    }
  };

  const renderContents = () => props.content.map(renderByType);
  return (
    <div className={props.className}>
      {renderContents()}
      <QuestionFooter
        className={`${props.className}__footer`}
        complete={props.complete}
        componentID={props.id}
        componentType={props.type}
        totalAttempts={props.totalAttempts}
        submitAnswer={props.submitAnswer}
        submitDisabled={setSubmitDisabled()}>
        <FooterFeedback
          showFeedback={props.showFeedback}
          isCorrect={isFeedbackCorrect()}
        />
      </QuestionFooter>
    </div>
  );
}

SliderSelectGroup.propTypes = {
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.oneOfType([
    PropTypes.oneOf(['question_color']),
    PropTypes.string,
  ]).isRequired,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number,
  /**
   * The prop that holds the text for the question
   */
  question: PropTypes.string.isRequired,

  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(['GROUPED_DNDQ']),
  /**
   * List of groups; Each group has different number of SequenceSliders
   */
  sliderGroups: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Unique id for the slider group
       */
      id: PropTypes.number,
      /**
       * Type of slider group
       */
      type: PropTypes.oneOf(['SEQUENCE_SLIDER_GROUP']),
      /**
       * list of sliders you wanna display on group
       */
      slider: PropTypes.arrayOf(PropTypes.object),
    })
  ),
  /**
   * Boolean value specifying if we should show feedback
   */
  showFeedback: PropTypes.bool.isRequired,
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * Callback function to be executed when selection sequence updated
   */
  updateSelection: PropTypes.func.isRequired,
};

SliderSelectGroup.defaultProps = {
  className: 'question_color',
  complete: false,
  content: [],
  question: '',
  totalAttempts: 0,
  type: 'DNDQ_COLOR',
  sliderGroups: [],
  showFeedback: false,
};
